import clsx from "clsx";
import { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
// @material-ui/core components
import { Card, CardContent, CardHeader, useMediaQuery } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import GenericButton from "components/Buttons/GenericButton";

import FormSelect from "components/FormFields/FormSelect";
import useFilterSync from "hooks/useFilterSync";
import useQueryParams from "hooks/useQueryParams";
import notificationsMap from "mappers/notificationsMap";
import { getDataSetByNameAction } from "redux/actions/datasetsAction";
import { getNotificationsAction } from "redux/actions/notificationsAction";
import { DATASET, PAGINATION } from "utils/const";
//import custom components
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import { getDatasetStateByName } from "utils/datasets";
import { getSelectOptionsElementByCode } from "utils/formSelect";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function FilterSection(props) {
  const classes = useStylesGeneric();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const submittedFilterRef = useRef(null);
  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    control,
    formState: { errors, isValid },
  } = useFormContext();

  const pageIndex = watch("pageIndex") ?? PAGINATION.FIRST_PAGE;
  const pageSize = watch("pageSize") ?? PAGINATION.PAGE_SIZE;

  const { getQueryParam } = useQueryParams();

  // Filter sync hook - sincroniza filtros e paginação com URL
  const filterSync = useFilterSync({
    defaultValues: props.defaultFormValues || {
      search: "",
      origin: null,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
    },
    syncOnMount: true,
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: props.defaultFormValues?.pageSize ?? PAGINATION.PAGE_SIZE,
    },
    onFilterChange: () => {
      props.setFiltersReady?.(true);
    },
    fieldSerializers: {
      origin: {
        toUrl: (value) => value?.code ?? null,
        fromUrl: (value) => value,
      },
    },
  });

  useEffect(() => {
    if (
      props.datasetsState[DATASET.ORIGIN] === undefined ||
      props.datasetsState[DATASET.ORIGIN]?.length === 0
    )
      props.getDataSetByName(DATASET.ORIGIN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restaurar o valor do select origin quando o dataset carregar
  useEffect(() => {
    const originFromUrl = getQueryParam("origin");
    const originDataset = getDatasetStateByName(props, DATASET.ORIGIN);
    
    if (originFromUrl !== undefined && originFromUrl !== null && originDataset?.length > 0) {
      const originCode = String(originFromUrl);
      const originObject =
        originDataset.find((o) => String(o?.code) === originCode) ||
        getSelectOptionsElementByCode(originDataset, parseInt(originCode, 10));
      if (originObject) {
        setValue("origin", originObject);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.ORIGIN]]);

  const cleanFilter = () => {
    const nextFormValues = {
      search: "",
      origin: null,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
    };

    reset(nextFormValues);
    
    // Limpa URL
    filterSync.handleFilterReset();

    const next = {
      ...notificationsMap(nextFormValues),
      lastXNotifications: 1000,
      message: nextFormValues.search,
      originId: nextFormValues.origin?.code ?? null,
      onlyUnread: false,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
    };

    submittedFilterRef.current = next;
    props.getNotifications(next);
  };

  const onSubmit = (data) => {
    if (isValid) {
      const nextFormValues = {
        ...data,
        pageIndex: PAGINATION.FIRST_PAGE,
        pageSize: data.pageSize ?? PAGINATION.PAGE_SIZE,
      };

      // Sincroniza filtros com URL e reseta paginação
      if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) !== PAGINATION.FIRST_PAGE) {
        setValue("pageIndex", PAGINATION.FIRST_PAGE);
      }

      filterSync.handleFilterSubmit(nextFormValues);
      
      const next = {
        ...notificationsMap(nextFormValues),
        lastXNotifications: 1000,
        message: nextFormValues.search,
        originId: nextFormValues.origin?.code ?? null,
        onlyUnread: false,
        pageIndex: PAGINATION.FIRST_PAGE,
        pageSize: nextFormValues.pageSize,
      };

      submittedFilterRef.current = next;

      if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) === PAGINATION.FIRST_PAGE) {
        props.getNotifications(next);
      } else {
        setValue("pageIndex", PAGINATION.FIRST_PAGE);
      }
    }
  };

  useEffect(() => {
    if (props.filtersReady === false) return;

    if (!submittedFilterRef.current) {
      const values = getValues();
      submittedFilterRef.current = {
        ...notificationsMap(values),
        lastXNotifications: 1000,
        message: values.search,
        originId: values.origin?.code ?? null,
        onlyUnread: false,
        pageIndex,
        pageSize,
      };
    } else {
      submittedFilterRef.current = {
        ...submittedFilterRef.current,
        pageIndex,
        pageSize,
      };
    }

    props.getNotifications(submittedFilterRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, props.filtersReady]);

  // Mantém paginação sincronizada na URL
  useEffect(() => {
    if (props.filtersReady === false) return;
    filterSync.updatePagination(pageIndex, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, props.filtersReady]);

  return (
    <Card classes={{ root: classes.cardRoot }}>
      <CardHeader
        classes={{
          action: classes.cardHeaderAction,
          title: classes.cardHeader,
        }}
        title={
          "Pesquisa de Notificações"
        }
      ></CardHeader>
      <CardContent>
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Pesquisar</FormLabel>
                  <Controller
                    name="search"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                        classes={{
                          notchedOutline: clsx({
                            [classes.borderWarning]: errors["search"] !== undefined,
                          }),
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Origem</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="origin"
                    selectOptions={getDatasetStateByName(props, DATASET.ORIGIN)}
                    getValues={getValues}
                    required={false}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={12} classes={{ root: classes.textAlignRight }}>
                <Box
                  display="flex"
                  justifyContent={isSmDown ? "stretch" : "flex-end"}
                  alignItems={isSmDown ? "stretch" : "center"}
                  flexDirection={isSmDown ? "column" : "row"}
                  gridGap={12}
                >
                  <GenericButton
                    typeSubmit={false}
                    color="ghost"
                    onClick={() => {
                      cleanFilter();
                    }}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Limpar Filtros
                  </GenericButton>
                  <GenericButton
                    typeSubmit={true}
                    color="primary"
                    loading={props.notificationState?.notifications_Loading}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Pesquisar
                  </GenericButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </CardContent>
    </Card>
  );
}

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getDataSetByName: (name) => dispatch(getDataSetByNameAction(name)),
  getNotifications: (formData) => dispatch(getNotificationsAction(formData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterSection);
